<template>
  <section :key="'vod_player'" class="player-section-layout">
    <!-- 플레이어 콘텐츠 영역 -->
    <div class="video-wrap">
      <div class="player-area full">
        <TrailerPlayer
          v-if="hasTrailer"
          :content-info="contentInfo"
          current-section="vod"
          class="active"
        />
        <PosterHorizontal
          v-else-if="contentImage.isHorizontal"
          :poster-image="contentImage.image"
          :poster-alt-text="seasonTitle"
        />
        <PosterVertical
          v-else
          :poster-image="contentImage.image"
          :poster-alt-text="seasonTitle"
        />
      </div>
      <BtnToggleMuteTrailer
        v-if="isShowTrailerMuteBtn"
        :content-info="contentInfo"
      />

      <div class="video-detail-area">
        <h1 class="logo-box">
          <BrandBadge v-if="brandLogo" :brand-logo="brandLogo" />
          <em v-if="seasonTitleLogoImage" class="logo"
            ><img
              :src="titleLogImage(seasonTitleLogoImage)"
              width=""
              height=""
              :alt="seasonTitle"
          /></em>
          <em v-else class="title">{{ seasonTitle }}</em>
        </h1>

        <PlayerMetaData
          :key="`${seasonTitle}__meta_data`"
          :content-info="contentInfo"
          :content-type="contentType"
        />
        <PlayerComments
          :key="`${seasonTitle}__player_comments`"
          :comment-list="comments"
        />

        <div class="essential-box">
          <div v-if="hasSeason" class="select-box">
            <PlayerFilterList
              :key="`${seasonTitle}__season_list`"
              :current-id="programId"
              :filter-item="seasonList"
              :base-api="seasonList.baseApi"
            />
          </div>
          <PlayerActionButton
            :key="`${seasonTitle}__player_action_button`"
            :content-info="contentInfo"
            :landing-info="landingInfo"
            :content-type="contentType"
            :permission="permission"
          />
          <PlayerIconButtons
            v-if="!isWavveOn"
            :key="`${seasonTitle}__icon_button`"
            :content-info="contentInfo"
            :content-type="contentType"
            @on-click-share="openSharePopup"
          />
        </div>
        <permission-notice :permission="permission" style="margin-top: 24px" />
        <PlayerDetail
          :key="`${seasonTitle}__detail_info`"
          :contents-status="contentsStatus"
          :content-info="contentInfo"
          :content-type="contentType"
          @on-click-more="moveToDetailInfo"
        />

        <PlayerNotice
          :key="`${seasonTitle}__notice`"
          :content-id="programId"
        />
      </div>
    </div>
  </section>
</template>

<script>
  import _ from 'lodash'
  import cookie from '@/common/utils/cookie'
  import { mapGetters } from 'vuex'
  import { MOVE_TO_DETAIL_TAB } from '@/common/resources/const'
  import {
    TRAILER_ATTR_BY_PLAYER_TYPE,
    PLAYER_TYPE,
  } from '@/common/resources/player.const'
  import { vodInfoBox, adjustHeight } from '@/common/resources/directives'
  import { addPostfixAndUrl, titleTagNameChange } from '@/common/utils/common'
  import { url } from '@/common/filters/url.filter'
  import { resizeThumbnail } from '@/common/utils/image'

  // mixins
  import PlayerDetailInfoMixin from '@/mixins/player.detail.info.mixin'
  import TrailerPlayerPageMixin from '@/mixins/trailer.player.page.mixin'

  // components
  import BrandBadge from '@/components/player/BrandBadge'
  import BtnToggleMuteTrailer from '@/components/player/BtnToggleMuteTrailer'
  import PlayerActionButton from '@/components/player/detail/PlayerActionButton'
  import PlayerComments from '@/components/player/detail/PlayerComments'
  import PlayerDetail from '@/components/player/detail/PlayerDetail'
  import PlayerFilterList from '@/components/player/PlayerFilterList'
  import PlayerIconButtons from '@/components/player/detail/PlayerIconButtons'
  import PlayerMetaData from '@/components/player/detail/PlayerMetaData'
  import PlayerNotice from '@/components/player/PlayerNotice'
  import PosterHorizontal from '@/components/player/PosterHorizontal'
  import PosterVertical from '@/components/player/PosterVertical'
  import TrailerPlayer from '@/components/player/TrailerPlayer'
  import PermissionNotice from '@/components/player/PermissionNotice.vue'
  import { getPermissionForContent } from '@/common/utils/request'

  export default {
    name: 'VodPlayer',
    components: {
      PermissionNotice,
      PlayerMetaData,
      PlayerFilterList,
      PlayerActionButton,
      PlayerNotice,
      BrandBadge,
      PosterVertical,
      PosterHorizontal,
      TrailerPlayer,
      BtnToggleMuteTrailer,
      PlayerIconButtons,
      PlayerDetail,
      PlayerComments,
    },
    filters: {
      addPostfixAndUrl,
      url,
    },
    directives: {
      vodInfoBox,
      adjustHeight,
    },
    mixins: [PlayerDetailInfoMixin, TrailerPlayerPageMixin],
    props: {
      contentInfo: {
        type: Object,
        default: () => ({}),
      },
      landingInfo: {
        type: Object,
        default: () => ({}),
      },
      dataOrderInfo: {
        type: Object,
      },
      openShareLayout: {
        type: Function,
      },
      openSharePopup: {
        type: Function,
      },
    },
    data() {
      return {
        isWavveOn: cookie.isWavveOn(),
        contentType: PLAYER_TYPE.VOD,
        permission: {},
      }
    },
    computed: {
      ...mapGetters(['currentMenu']),
      brandLogo() {
        return _.get(this.contentInfo, 'brand_logolist.0', '')
      },
      contentsStatus() {
        return _.get(this.landingInfo, 'contents_status', '')
      },
      comment() {
        return _.get(this.contentInfo, 'comment', '')
      },
      comments() {
        const { autocomment = '' } = this.contentInfo
        return [autocomment, this.comment]
      },
      contentImage() {
        // 우선 순위 1. 세로형/로고n  2. 가로형/로고y  3. 세로형/로고y
        const {
          season_horizontal_logoN_image: imageHorizonWithoutLogo = '',
          season_vertical_logoY_image: imageVerticalWithLogo = '',
          season_horizontal_logoY_image: imageHorizonWithLogo = '',
        } = this.contentInfo
        const hasVerticalLogoImage =
          !imageVerticalWithLogo && imageHorizonWithLogo

        let isHorizontal = imageHorizonWithoutLogo.length > 0
        if (hasVerticalLogoImage) isHorizontal = true

        const image =
          imageHorizonWithoutLogo ||
          imageVerticalWithLogo ||
          imageHorizonWithLogo
        return { isHorizontal, image }
      },
      programId() {
        return this.contentInfo?.programid || ''
      },
      seasonTitle() {
        return this.contentInfo?.seasontitle || ''
      },
      seasonTitleLogoImage() {
        return this.contentInfo?.seasontitlelogoimage || ''
      },
      seasonList() {
        const { season_list: seasonList = {} } = this.contentInfo
        const {
          count = '0',
          base_api: baseApi = '',
          season_item_list: seasonItemList = [],
        } = seasonList

        return { count, baseApi, seasonItemList }
      },
      hasSeason() {
        return Number(this.seasonList.count) > 1
      },
      hasTrailer() {
        return (
          _.get(this.contentInfo, TRAILER_ATTR_BY_PLAYER_TYPE['vod'].id, '') !==
          ''
        )
      },
      /**
       * 예고편 플레이어 음소거 버튼 노출 여부 (트레일러가 있고 재생상태라면)
       * @returns {false|boolean}
       */
      isShowTrailerMuteBtn() {
        return this.hasTrailer && this.isTrailerPlaying
      },
    },
    watch: {
      contentInfo: {
        async handler() {
          this.setPageTitle()
          const _contentType = this.contentInfo?.type === 'onair' ? 'qvod' : 'vod'
          const response = await getPermissionForContent({
            contenttype: _contentType,
            contentid: this.contentInfo.contentid ?? '',
          })
          this.permission = response.data
        },
        immediate: true,
      },
    },
    mounted() {
      this.setPageTitle()
    },
    methods: {
      setPageTitle() {
        titleTagNameChange(
          `${this.seasonTitle || '프로그램 상세화면'} : 웨이브`,
        )
      },
      titleLogImage(img) {
        return resizeThumbnail(img, {
          width: 0,
          height: 0,
          option: 20,
          quality: 80,
        })
      },
      moveToDetailInfo() {
        this.$events.$emit(MOVE_TO_DETAIL_TAB)
      },
    },
  }
</script>
<style scoped>
  .player-section-layout {
    position: relative;
    margin: 0 100px;
  }
  .full {
    width: 100%;
  }
</style>
